/**
 * Email Notification Job Service
 * 
 * Manages multiple email notification jobs per partner
 */

import pb from '../lib/pocketbase';
import { EmailNotificationJob, EmailNotificationEvent } from '../types';

export class EmailNotificationJobService {
  private static collection = 'email_notification_jobs';

  /**
   * Get all email notification jobs for a partner
   */
  static async getJobs(partnerId: string): Promise<EmailNotificationJob[]> {
    try {
      const records = await pb.collection(this.collection).getFullList({
        filter: `partner_id = "${partnerId}"`,
        sort: '-created',
      });
      
      return records.map(record => this.mapRecord(record));
    } catch (error) {
      console.error('Failed to get email notification jobs:', error);
      return [];
    }
  }

  /**
   * Get a single email notification job by ID
   */
  static async getJob(jobId: string): Promise<EmailNotificationJob | null> {
    try {
      const record = await pb.collection(this.collection).getOne(jobId);
      return this.mapRecord(record);
    } catch (error) {
      console.error('Failed to get email notification job:', error);
      return null;
    }
  }

  /**
   * Create a new email notification job
   */
  static async createJob(
    partnerId: string,
    jobData: {
      job_name: string;
      recipient_emails: string[];
      event_subscriptions: EmailNotificationEvent[];
      enabled?: boolean;
      created_by?: string;
    }
  ): Promise<EmailNotificationJob | null> {
    try {
      const data = {
        partner_id: partnerId,
        job_name: jobData.job_name,
        recipient_emails: JSON.stringify(jobData.recipient_emails),
        event_subscriptions: JSON.stringify(jobData.event_subscriptions),
        enabled: jobData.enabled ?? true,
        created_by: jobData.created_by || '',
      };

      const record = await pb.collection(this.collection).create(data);
      return this.mapRecord(record);
    } catch (error) {
      console.error('Failed to create email notification job:', error);
      return null;
    }
  }

  /**
   * Update an existing email notification job
   */
  static async updateJob(
    jobId: string,
    jobData: {
      job_name?: string;
      recipient_emails?: string[];
      event_subscriptions?: EmailNotificationEvent[];
      enabled?: boolean;
    }
  ): Promise<EmailNotificationJob | null> {
    try {
      const data: any = {};
      
      if (jobData.job_name !== undefined) data.job_name = jobData.job_name;
      if (jobData.recipient_emails !== undefined) data.recipient_emails = JSON.stringify(jobData.recipient_emails);
      if (jobData.event_subscriptions !== undefined) data.event_subscriptions = JSON.stringify(jobData.event_subscriptions);
      if (jobData.enabled !== undefined) data.enabled = jobData.enabled;

      const record = await pb.collection(this.collection).update(jobId, data);
      return this.mapRecord(record);
    } catch (error) {
      console.error('Failed to update email notification job:', error);
      return null;
    }
  }

  /**
   * Delete an email notification job
   */
  static async deleteJob(jobId: string): Promise<boolean> {
    try {
      await pb.collection(this.collection).delete(jobId);
      return true;
    } catch (error) {
      console.error('Failed to delete email notification job:', error);
      return false;
    }
  }

  /**
   * Toggle job enabled status
   */
  static async toggleJob(jobId: string, enabled: boolean): Promise<boolean> {
    try {
      await pb.collection(this.collection).update(jobId, { enabled });
      return true;
    } catch (error) {
      console.error('Failed to toggle email notification job:', error);
      return false;
    }
  }

  /**
   * Map PocketBase record to EmailNotificationJob
   */
  private static mapRecord(record: any): EmailNotificationJob {
    return {
      id: record.id,
      partner_id: record.partner_id,
      job_name: record.job_name,
      recipient_emails: typeof record.recipient_emails === 'string' 
        ? JSON.parse(record.recipient_emails) 
        : record.recipient_emails || [],
      event_subscriptions: typeof record.event_subscriptions === 'string'
        ? JSON.parse(record.event_subscriptions)
        : record.event_subscriptions || [],
      enabled: record.enabled ?? true,
      created_at: record.created,
      updated_at: record.updated,
      created_by: record.created_by || undefined,
    };
  }
}
